import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  CircularProgress,
  Box,
  Paper,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Chip
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';
import DashboardTemplate from '../../Common/DashboardTemplate';
import { ServiceLocator } from '../../../Services/ServiceLocator';
import { getCurrentLocalTime, formatForDisplay } from '../../../utils/dateUtils';

const Analytics = () => {
  const [summaryData, setSummaryData] = useState(null);
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(getCurrentLocalTime().format('YYYY-MM-DD'));
  const [timeRange, setTimeRange] = useState('24'); // Default to 24 months for monthly interval
  const [interval, setInterval] = useState('month');
  const [startDate, setStartDate] = useState(getCurrentLocalTime().subtract(24, 'month').format('YYYY-MM-DD')); // 24 months ago
  const [endDate, setEndDate] = useState(getCurrentLocalTime().format('YYYY-MM-DD'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [showTimeRangeControls, setShowTimeRangeControls] = useState(true);

  // Average weeks inventory calculation state
  const [averageWeeksInventoryOpen, setAverageWeeksInventoryOpen] = useState(false);
  const [averageWeeksInventoryResult, setAverageWeeksInventoryResult] = useState(null);
  const [averageWeeksInventoryLoading, setAverageWeeksInventoryLoading] = useState(false);
  const [averageWeeksInventoryDate, setAverageWeeksInventoryDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [averageWeeksInventoryPartFilters, setAverageWeeksInventoryPartFilters] = useState({});
  const [averageWeeksInventoryTimeSeriesData, setAverageWeeksInventoryTimeSeriesData] = useState(null);
  const [averageWeeksInventoryTimeSeriesLoading, setAverageWeeksInventoryTimeSeriesLoading] = useState(false);
  const [partsList, setPartsList] = useState([]);
  const [partsListLoading, setPartsListLoading] = useState(false);
  const [partsSearchTerm, setPartsSearchTerm] = useState('');

  const analyticsService = ServiceLocator.Instance.getAnalyticsService();

  const fetchCurrentSummary = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await analyticsService.getCurrentInventorySummary();
      // SQL already rounds to DECIMAL(18,2) - trust SQL's rounding, don't round again
      // Double-rounding can cause discrepancies due to floating-point precision
      setSummaryData(data);
      
      // Always calculate current average weeks inventory for selected parts
      try {
        const selectedPartIds = Object.keys(averageWeeksInventoryPartFilters)
          .filter(partId => averageWeeksInventoryPartFilters[partId]);
        const avgWeeksResult = await analyticsService.getAverageWeeksInventory(selectedDate, selectedPartIds);
        setAverageWeeksInventoryResult(avgWeeksResult);
      } catch (err) {
        console.warn('Failed to calculate current average weeks inventory:', err);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch current inventory summary');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeSeries = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await analyticsService.getInventoryTimeSeries(startDate, endDate, interval);
      // SQL already rounds to DECIMAL(18,2) - trust SQL's rounding, don't round again
      setTimeSeriesData(data.dataPoints || []);
      
      // Also fetch average weeks inventory time series for selected parts
      try {
        const selectedPartIds = Object.keys(averageWeeksInventoryPartFilters)
          .filter(partId => averageWeeksInventoryPartFilters[partId]);
        const avgWeeksData = await analyticsService.getAverageWeeksInventoryTimeSeries(startDate, endDate, interval, selectedPartIds);
        setAverageWeeksInventoryTimeSeriesData(avgWeeksData);
      } catch (err) {
        console.warn('Failed to fetch average weeks inventory time series:', err);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch time series data');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      setError('');
      await analyticsService.exportInventorySummary(selectedDate);
    } catch (err) {
      setError(err.message || 'Failed to export data');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Average weeks inventory calculation functions
  const loadPartsList = async () => {
    try {
      setPartsListLoading(true);
      const partService = ServiceLocator.Instance.getPartService();
      const parts = await partService.getAllPartsExpanded();
      
      // Include only inventory items (active/inactive, all revisions)
      const filteredParts = parts.filter(part => part.part.isInventoryItem);
      
      // Sort parts by partId
      const sortedParts = filteredParts.sort((a, b) => a.part.partId.localeCompare(b.part.partId));
      setPartsList(sortedParts);
      
      // Load saved filters from sessionStorage
      const savedFilters = sessionStorage.getItem('averageWeeksInventoryPartFilters');
      if (savedFilters) {
        const parsedFilters = JSON.parse(savedFilters);
        setAverageWeeksInventoryPartFilters(parsedFilters);
      } else {
        // Default all parts to checked
        const defaultFilters = {};
        sortedParts.forEach(part => {
          defaultFilters[part.part.id] = true;
        });
        setAverageWeeksInventoryPartFilters(defaultFilters);
      }
    } catch (err) {
      setError(err.message || 'Failed to load parts list');
      setSnackbarOpen(true);
    } finally {
      setPartsListLoading(false);
    }
  };

  const handlePartFilterChange = (partId, checked) => {
    const newFilters = { ...averageWeeksInventoryPartFilters };
    newFilters[partId] = checked;
    setAverageWeeksInventoryPartFilters(newFilters);
    
    // Save to sessionStorage
    sessionStorage.setItem('averageWeeksInventoryPartFilters', JSON.stringify(newFilters));
  };

  const calculateAverageWeeksInventory = async () => {
    try {
      setAverageWeeksInventoryLoading(true);
      setError('');
      
      const selectedPartIds = Object.keys(averageWeeksInventoryPartFilters)
        .filter(partId => averageWeeksInventoryPartFilters[partId]);
      
      const result = await analyticsService.getAverageWeeksInventory(averageWeeksInventoryDate, selectedPartIds);
      setAverageWeeksInventoryResult(result);
    } catch (err) {
      setError(err.message || 'Failed to calculate average weeks inventory');
      setSnackbarOpen(true);
    } finally {
      setAverageWeeksInventoryLoading(false);
    }
  };

  const calculateAverageWeeksInventoryTimeSeries = async () => {
    try {
      setAverageWeeksInventoryTimeSeriesLoading(true);
      setError('');
      
      const selectedPartIds = Object.keys(averageWeeksInventoryPartFilters)
        .filter(partId => averageWeeksInventoryPartFilters[partId]);
      
      const result = await analyticsService.getAverageWeeksInventoryTimeSeries(startDate, endDate, interval, selectedPartIds);
      setAverageWeeksInventoryTimeSeriesData(result);
    } catch (err) {
      setError(err.message || 'Failed to calculate average weeks inventory time series');
      setSnackbarOpen(true);
    } finally {
      setAverageWeeksInventoryTimeSeriesLoading(false);
    }
  };

  const openAverageWeeksInventoryDialog = () => {
    setAverageWeeksInventoryOpen(true);
    loadPartsList();
  };

  // Helper functions for parts filtering and selection
  const getFilteredParts = () => {
    if (!partsSearchTerm) return partsList;
    return partsList.filter(part => 
      part.part.partId.toLowerCase().includes(partsSearchTerm.toLowerCase()) ||
      part.part.description.toLowerCase().includes(partsSearchTerm.toLowerCase())
    );
  };

  const getSelectedPartsCount = () => {
    return Object.values(averageWeeksInventoryPartFilters).filter(Boolean).length;
  };

  const handleSelectAll = () => {
    const filteredParts = getFilteredParts();
    const newFilters = { ...averageWeeksInventoryPartFilters };
    filteredParts.forEach(part => {
      newFilters[part.part.id] = true;
    });
    setAverageWeeksInventoryPartFilters(newFilters);
    sessionStorage.setItem('averageWeeksInventoryPartFilters', JSON.stringify(newFilters));
  };

  const handleSelectNone = () => {
    const filteredParts = getFilteredParts();
    const newFilters = { ...averageWeeksInventoryPartFilters };
    filteredParts.forEach(part => {
      newFilters[part.part.id] = false;
    });
    setAverageWeeksInventoryPartFilters(newFilters);
    sessionStorage.setItem('averageWeeksInventoryPartFilters', JSON.stringify(newFilters));
  };

  const handlePartsInventoryExport = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Get both the summary (for totals) and individual parts (for CSV rows)
      // Use summary to get exact totals that match Analytics display
      const [summary, partsInventory] = await Promise.all([
        analyticsService.getCurrentInventorySummary(),
        analyticsService.getCurrentPartsInventorySummary()
      ]);

      if (!partsInventory || partsInventory.length === 0) {
        setError('No inventory items to export.');
        setSnackbarOpen(true);
        return;
      }

      // Get part details (partId, brand, revision) from parts list
      const partsMap = new Map();
      if (partsList.length > 0) {
        partsList.forEach(pe => {
          if (pe.part && pe.part.id) {
            partsMap.set(pe.part.id, pe.part);
          }
        });
      } else {
        // If parts list not loaded, fetch it
        const partService = ServiceLocator.Instance.getPartService();
        const allParts = await partService.getAllPartsExpanded();
        allParts.forEach(pe => {
          if (pe.part && pe.part.id) {
            partsMap.set(pe.part.id, pe.part);
          }
        });
      }

      // Get vendors map for vendor names (used when brand is empty)
      const vendorService = ServiceLocator.Instance.getVendorService();
      const vendors = await vendorService.getAllVendors();
      const vendorsMap = new Map();
      vendors.forEach(vendor => {
        if (vendor.id) {
          vendorsMap.set(vendor.id, vendor.name || vendor.vendorName || '');
        }
      });

      // CSV headers
      const csvHeaders = [
        'Part Number',
        'Manufacturer',
        'Cost',
        'Qty on Hand',
        'Value'
      ];

      // Get summary totals for comparison
      // Analytics displays: partsPackaged, partsUnpackaged, and totalRawValue
      // But the description says "Total value of all parts on hand" (parts only)
      const summaryTotalPackaged = summary.partsPackaged || 0;
      const summaryTotalUnpackaged = summary.partsUnpackaged || 0;
      const summaryTotalValue = summary.totalValue || 0; // Parts only value from SQL

      // Build CSV rows from API data (already filtered and calculated by backend SQL)
      // Sort alphabetically by part number
      const sortedPartsInventory = [...partsInventory].sort((a, b) => {
        const partA = partsMap.get(a.partId);
        const partB = partsMap.get(b.partId);
        const partNumberA = partA ? `${partA.partId || ''} rev ${partA.revision || ''}` : a.partNumber;
        const partNumberB = partB ? `${partB.partId || ''} rev ${partB.revision || ''}` : b.partNumber;
        return partNumberA.localeCompare(partNumberB);
      });

      // Calculate totals from individual rows to compare with summary
      let calculatedTotalPackaged = 0;
      let calculatedTotalUnpackaged = 0;
      let calculatedTotalQty = 0;
      let calculatedTotalValue = 0;
      // Track unrounded values for comparison
      let unroundedTotalValue = 0;

      const csvRows = sortedPartsInventory.map(partInv => {
        const part = partsMap.get(partInv.partId);
        const partNumber = part ? `${part.partId || ''} rev ${part.revision || ''}` : partInv.partNumber;
        // Use brand if available, otherwise use selected vendor name
        let manufacturer = '';
        if (part) {
          manufacturer = part.brand || '';
          if (!manufacturer && part.selectedVendorId) {
            manufacturer = vendorsMap.get(part.selectedVendorId) || '';
          }
        }

        // Use totalQuantity for individual row display
        // Round quantities to integers to match SQL INT type
        const qtyOnHand = Math.round(partInv.totalQuantity);
        const packagedQty = Math.round(partInv.packagedQuantity || 0);
        const unpackagedQty = Math.round(partInv.unpackagedQuantity || 0);
        const unitCost = Number(partInv.unitCost);
        
        // Calculate value: TotalQuantity * UnitCost (matches SQL: SUM(TotalQuantity * UnitCost))
        // SQL multiplies each row (no rounding), sums all products, then rounds the final sum once
        // For CSV: Display rounded values in individual rows, but calculate total from unrounded sum
        // This ensures SUM(E2:E744) in Excel matches the TOTAL row
        const unroundedValue = unitCost * qtyOnHand;
        const value = Math.round(unroundedValue * 100) / 100; // Rounded for display

        // Accumulate totals from individual rows
        calculatedTotalPackaged += packagedQty;
        calculatedTotalUnpackaged += unpackagedQty;
        calculatedTotalQty += qtyOnHand;
        calculatedTotalValue += value; // Rounded sum (for comparison)
        unroundedTotalValue += unroundedValue; // Unrounded sum - use this for total to match SQL

        return [
          partNumber,
          manufacturer,
          unitCost.toFixed(2),
          qtyOnHand.toFixed(0),
          value.toFixed(2)
        ];
      });

      // Use SQL summary totals - the authoritative source for financial accuracy
      // SQL: SUM(TotalQuantity * UnitCost) then CAST to DECIMAL(18,2) - this is the correct value
      // Note: Excel's SUM(E2:E744) will sum rounded display values and may differ slightly
      // due to cumulative rounding errors, but SQL's calculation is the accurate source
      const totalQty = summaryTotalPackaged + summaryTotalUnpackaged;
      const totalValue = summaryTotalValue; // SQL's accurate calculation (matches Analytics.js)
      
      // Calculate average cost (weighted by quantity)
      const averageCost = totalQty > 0 ? totalValue / totalQty : 0;

      // Add totals row
      csvRows.push([
        'TOTAL',
        '',
        '',
        totalQty.toFixed(0),
        totalValue.toFixed(2)
      ]);

      // Create CSV content
      const csvContent = [csvHeaders, ...csvRows]
        .map(row => row.map(cell => `"${cell}"`).join(','))
        .join('\n');

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      const date = format(new Date(), 'yyyy-MM-dd');
      link.setAttribute('download', `inventory_report_${date}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export inventory report:', err);
      setError('Failed to export inventory report. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleChartDataExport = () => {
    try {
      if (!timeSeriesData || timeSeriesData.length === 0) {
        setError('No chart data available to export');
        setSnackbarOpen(true);
        return;
      }

      // Create CSV content with date, values, quantities, and average weeks
      const csvHeaders = [
        'Date', 
        'Total Value', 
        'Packaged Value', 
        'Unpackaged Value', 
        'Total Packaged', 
        'Total Unpackaged', 
        'Packaged Assembly Count', 
        'Packaged Assembly Value',
        'Average Weeks Inventory'
      ];
      
      const csvRows = timeSeriesData.map(dataPoint => {
        // Find matching average weeks data for this date
        const avgWeeksData = averageWeeksInventoryTimeSeriesData?.dataPoints?.find(
          avgPoint => format(new Date(avgPoint.calculationDate), 'yyyy-MM-dd') === 
                     format(new Date(dataPoint.summaryDate), 'yyyy-MM-dd')
        );
        
        return [
          format(new Date(dataPoint.summaryDate), 'yyyy-MM-dd'),
          dataPoint.totalValue || 0, // Parts only value (matches "Total Value On Hand" card)
          dataPoint.packagedValue || 0,
          dataPoint.unpackagedValue || 0,
          dataPoint.partsPackaged || 0,
          dataPoint.partsUnpackaged || 0,
          dataPoint.assembliesPackaged || 0,
          dataPoint.assembliesMarketValue || 0,
          avgWeeksData ? avgWeeksData.averageWeeksOnHand.toFixed(2) : 'N/A'
        ];
      });

      const csvContent = [csvHeaders, ...csvRows]
        .map(row => row.map(cell => `"${cell}"`).join(','))
        .join('\n');

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `inventory_analytics_${format(new Date(), 'yyyy-MM-dd')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError('Failed to export chart data');
      setSnackbarOpen(true);
    }
  };

  useEffect(() => {
    fetchCurrentSummary();
    loadPartsList(); // Load parts list on component mount
  }, []);

  // Removed automatic fetchTimeSeries on field changes - now only called when Update Chart button is pressed

  const formatCurrency = (value) => {
    // SQL already rounds to DECIMAL(18,2) - trust SQL's rounding
    // Intl.NumberFormat will format the value as-is (SQL values are already properly rounded)
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Number(value) || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const formatThousands = (value) => {
    return `${(value / 10000).toFixed(0)}0k`;
  };

  const getQuantityChartYAxisProps = () => {
    if (!timeSeriesData || timeSeriesData.length === 0) {
      return {
        domain: [0, 'auto'],
        tickFormatter: formatThousands
      };
    }

         // Find the maximum value across partsPackaged, partsUnpackaged, and assembliesPackaged
     const maxValue = Math.max(
       ...timeSeriesData.map(d => Math.max(
         d.partsPackaged || 0, 
         d.partsUnpackaged || 0, 
         d.assembliesPackaged || 0
       ))
     );

    if (maxValue < 500000) {
      // For values less than 500k, show ticks every 50k
      const maxTick = Math.ceil(maxValue / 50000) * 50000;
      return {
        domain: [0, maxTick],
        ticks: Array.from({ length: Math.floor(maxTick / 50000) + 1 }, (_, i) => i * 50000),
        tickFormatter: formatThousands
      };
    } else if (maxValue <= 1000000) {
      // For values up to 1M, show ticks every 100k
      const maxTick = Math.ceil(maxValue / 100000) * 100000;
      return {
        domain: [0, maxTick],
        ticks: Array.from({ length: Math.floor(maxTick / 100000) + 1 }, (_, i) => i * 100000),
        tickFormatter: formatThousands
      };
    } else {
      // For values over 1M, use auto scaling
      return {
        domain: [0, 'auto'],
        tickFormatter: formatThousands
      };
    }
  };

  const getTotalValueChartYAxisProps = () => {
    if (!timeSeriesData || timeSeriesData.length === 0) {
      return {
        domain: [0, 'auto'],
        tickFormatter: (value) => `$${(value / 1000000).toFixed(1)}M`
      };
    }

    // Find the maximum value for totalValue (parts only, matches "Total Value On Hand" card)
    const maxValue = Math.max(
      ...timeSeriesData.map(d => d.totalValue || 0)
    );

    if (maxValue <= 500000) {
      // For values up to $500k, show ticks every $100k
      const maxTick = Math.ceil(maxValue / 100000) * 100000;
      return {
        domain: [0, maxTick],
        ticks: Array.from({ length: Math.floor(maxTick / 100000) + 1 }, (_, i) => i * 100000),
        tickFormatter: (value) => `$${(value / 1000).toFixed(0)}k`
      };
    } else if (maxValue <= 1000000) {
      // For values up to $1M, show ticks every $250k
      const maxTick = Math.ceil(maxValue / 250000) * 250000;
      return {
        domain: [0, maxTick],
        ticks: Array.from({ length: Math.floor(maxTick / 250000) + 1 }, (_, i) => i * 250000),
        tickFormatter: (value) => `$${(value / 1000).toFixed(0)}k`
      };
    } else if (maxValue <= 2500000) {
      // For values up to $2.5M, show ticks every $500k
      const maxTick = Math.ceil(maxValue / 500000) * 500000;
      return {
        domain: [0, maxTick],
        ticks: Array.from({ length: Math.floor(maxTick / 500000) + 1 }, (_, i) => i * 500000),
        tickFormatter: (value) => `$${(value / 1000000).toFixed(1)}M`
      };
    } else {
      // For values over $2.5M, show ticks every $1M
      const maxTick = Math.ceil(maxValue / 1000000) * 1000000;
      return {
        domain: [0, maxTick],
        ticks: Array.from({ length: Math.floor(maxTick / 1000000) + 1 }, (_, i) => i * 1000000),
        tickFormatter: (value) => `$${(value / 1000000).toFixed(0)}M`
      };
    }
  };

  const getPackagedUnpackagedChartYAxisProps = () => {
    if (!timeSeriesData || timeSeriesData.length === 0) {
      return {
        domain: [0, 'auto'],
        tickFormatter: (value) => `$${(value / 1000000).toFixed(1)}M`
      };
    }

         // Find the maximum value for packagedValue, unpackagedValue, and assembliesMarketValue
     const maxValue = Math.max(
       ...timeSeriesData.map(d => Math.max(
         d.packagedValue || 0,
         d.unpackagedValue || 0,
         d.assembliesMarketValue || 0
       ))
     );

    if (maxValue <= 500000) {
      // For values up to $500k, show ticks every $100k
      const maxTick = Math.ceil(maxValue / 100000) * 100000;
      return {
        domain: [0, maxTick],
        ticks: Array.from({ length: Math.floor(maxTick / 100000) + 1 }, (_, i) => i * 100000),
        tickFormatter: (value) => `$${(value / 1000).toFixed(0)}k`
      };
    } else if (maxValue <= 1000000) {
      // For values up to $1M, show ticks every $250k
      const maxTick = Math.ceil(maxValue / 250000) * 250000;
      return {
        domain: [0, maxTick],
        ticks: Array.from({ length: Math.floor(maxTick / 250000) + 1 }, (_, i) => i * 250000),
        tickFormatter: (value) => `$${(value / 1000).toFixed(0)}k`
      };
    } else if (maxValue <= 2500000) {
      // For values up to $2.5M, show ticks every $500k
      const maxTick = Math.ceil(maxValue / 500000) * 500000;
      return {
        domain: [0, maxTick],
        ticks: Array.from({ length: Math.floor(maxTick / 500000) + 1 }, (_, i) => i * 500000),
        tickFormatter: (value) => `$${(value / 1000000).toFixed(1)}M`
      };
    } else {
      // For values over $2.5M, show ticks every $1M
      const maxTick = Math.ceil(maxValue / 1000000) * 1000000;
      return {
        domain: [0, maxTick],
        ticks: Array.from({ length: Math.floor(maxTick / 1000000) + 1 }, (_, i) => i * 1000000),
        tickFormatter: (value) => `$${(value / 1000000).toFixed(0)}M`
      };
    }
  };

  const getTimeRangeDates = (months) => {
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - parseInt(months));
    return {
      start: format(start, 'yyyy-MM-dd'),
      end: format(end, 'yyyy-MM-dd')
    };
  };

  const getYearDates = (year) => {
    return {
      start: `${year}-01-01`,
      end: `${year}-12-31`
    };
  };

  const handleIntervalChange = (newInterval) => {
    setInterval(newInterval);
    setShowTimeRangeControls(true);
    
    // Reset time range based on interval limits
    let defaultTimeRange = '12';
    if (newInterval === 'day') {
      defaultTimeRange = '1'; // 2 months max for daily
    } else if (newInterval === 'week') {
      defaultTimeRange = '3'; // 1 year max for weekly
    } else if (newInterval === 'month') {
      defaultTimeRange = '12'; // 24 months max for monthly
    } else if (newInterval === 'year') {
      defaultTimeRange = '24'; // 60 months max for yearly
    }
    
    setTimeRange(defaultTimeRange);
    const dates = getTimeRangeDates(defaultTimeRange);
    setStartDate(dates.start);
    setEndDate(dates.end);
  };



  const handleTimeRangeChange = (value) => {
    setTimeRange(value);
    
    if (value.startsWith('year-')) {
      // Handle year selection
      const year = value.replace('year-', '');
      const dates = getYearDates(year);
      setStartDate(dates.start);
      setEndDate(dates.end);
    } else {
      // Handle regular time range selection
      const dates = getTimeRangeDates(value);
      setStartDate(dates.start);
      setEndDate(dates.end);
    }
  };

  const getTimeRangeOptions = () => {
    const options = [];
    
    // Add time range options based on interval
    if (interval === 'day') {
      options.push(
        { value: '1', label: '1 Month' },
        { value: '2', label: '2 Months' }
      );
    } else if (interval === 'week') {
      options.push(
        { value: '1', label: '1 Months' },
        { value: '2', label: '2 Months' },
        { value: '3', label: '3 Months' },
        { value: '6', label: '6 Months' },
        { value: '9', label: '9 Months' },
        { value: '12', label: '1 Year' }
      );
    } else if (interval === 'month') {
      options.push(
        { value: '3', label: '3 Months' },
        { value: '6', label: '6 Months' },
        { value: '9', label: '9 Months' },
        { value: '12', label: '12 Months' },
        { value: '18', label: '18 Months' },
        { value: '24', label: '24 Months' }
      );
    } else if (interval === 'year') {
      options.push(
        { value: '24', label: '2 Years' },
        { value: '36', label: '3 Years' },
        { value: '48', label: '4 Years' },
        { value: '60', label: '5 Years' }
      );
    }
    
    // Add past years options (except for daily interval)
    if (interval !== 'day') {
      options.push(
        { value: 'year-2025', label: '2025 (Jan 1 - Dec 31)' },
        { value: 'year-2024', label: '2024 (Jan 1 - Dec 31)' },
        { value: 'year-2023', label: '2023 (Jan 1 - Dec 31)' },
        { value: 'year-2022', label: '2022 (Jan 1 - Dec 31)' },
        { value: 'year-2021', label: '2021 (Jan 1 - Dec 31)' },
        { value: 'year-2020', label: '2020 (Jan 1 - Dec 31)' }
      );
    }
    
    return options;
  };

     const summaryCards = [
     {
       title: 'Total Packaged',
       value: summaryData ? formatNumber(summaryData.partsPackaged) : '0',
       color: '#1976d2',
       description: 'Parts packaged into assemblies'
     },
     {
       title: 'Total Unpackaged',
       value: summaryData ? formatNumber(summaryData.partsUnpackaged) : '0',
       color: '#f50057',
       description: 'Unpackaged parts in inventory'
     },
     {
       title: 'Packaged Assembly Count',
       value: summaryData ? formatNumber(summaryData.assembliesPackaged) : '0',
       color: '#4caf50',
       description: 'Number of packaged assemblies on hand'
     },
     {
       title: 'Packaged Value',
       value: summaryData ? formatCurrency(summaryData.packagedValue) : '$0.00',
       color: '#8bc34a',
       description: 'Value of packaged parts based on vendor pricing'
     },
     {
       title: 'Unpackaged Value',
       value: summaryData ? formatCurrency(summaryData.unpackagedValue) : '$0.00',
       color: '#ff9800',
       description: 'Value of unpackaged parts based on vendor pricing'
     },
           {
        title: 'Packaged Assembly Value',
        value: summaryData ? formatCurrency(summaryData.assembliesMarketValue) : '$0.00',
        color: '#9c27b0',
        description: 'Value of packaged assemblies based on MSRP'
      },
           {
        title: 'Total Value On Hand',
        value: summaryData ? formatCurrency(summaryData.totalValue) : '$0.00',
        color: '#673ab7',
        description: 'Total value of all parts on hand'
      },
      {
        title: 'Average Weeks Inventory',
        value: loading ? 'Calculating...' : 
               averageWeeksInventoryResult ? `${averageWeeksInventoryResult.averageWeeksOnHand.toFixed(1)} weeks` : 
               'Click "Update Summary" to calculate',
        color: '#ff5722',
                 description: 'Weeks of inventory on hand (selected parts)'
      }
   ];

  // Styles for better visibility on dark theme
  const controlStyles = {
    backgroundColor: '#ffffff',
    color: '#333333',
    border: '2px solid #1976d2',
    padding: 5,
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
    '& .MuiInputLabel-root': {
      color: '#1976d2',
      fontWeight: 'bold',
      fontSize: '14px',
    },
    '& .MuiInputBase-input': {
      color: '#333333',
      fontWeight: '500',
      fontSize: '14px',
    },
    '& .MuiInputLabel-root': {
      margin: 5
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#1976d2',
      borderWidth: '2px',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#1565c0',
      borderWidth: '2px',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#1976d2',
      borderWidth: '3px',
    },
    '& .MuiSelect-icon': {
      color: '#1976d2',
    },
  };

  const cardStyles = {
    backgroundColor: '#f5f5f5',
    color: '#333333',
    border: '3px solid #1976d2',
    marginBottom: '20px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    borderRadius: '12px',
  };

  return (
    <DashboardTemplate onSyncDatabase={fetchCurrentSummary} title="JXB Analytics">
      <div style={{ padding: '20px', maxWidth: 1400, margin: '0 auto' }}>
        <Typography variant="h4" gutterBottom style={{ color: '#ffffff', fontWeight: 'bold', marginBottom: '30px', textAlign: 'center', textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
          📊 Inventory Analytics Dashboard
        </Typography>
        
        {/* Controls */}
        <Card style={cardStyles}>
          <CardContent style={{ padding: '32px', backgroundColor: '#fafafa' }}>
            <Typography variant="h6" gutterBottom style={{ color: '#1976d2', marginBottom: '20px', fontWeight: 'bold', fontSize: '20px', textAlign: 'center' }}>
              📊 SUMMARY CONTROLS
            </Typography>
                         <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
               {/* First row - Date input */}
               <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
                 <TextField
                   label="Selected Date"
                   type="date"
                   value={selectedDate}
                   onChange={(e) => setSelectedDate(e.target.value)}
                   InputLabelProps={{ shrink: true }}
                   style={{ ...controlStyles, minWidth: '200px', flex: '0 0 auto' }}
                   InputProps={{
                     style: { color: '#333333' }
                   }}
                 />
                 <Button
                   variant="contained"
                   color="primary"
                   onClick={fetchCurrentSummary}
                   disabled={loading}
                   style={{ height: '56px', minWidth: '200px' }}
                 >
                   {loading ? <CircularProgress size={24} /> : 'Update Summary'}
                 </Button>
                 <Button
                   variant="contained"
                   color="secondary"
                   onClick={openAverageWeeksInventoryDialog}
                   style={{ height: '56px', minWidth: '200px' }}
                 >
                   📊 Effective Inventory Parts ({getSelectedPartsCount()})
                 </Button>
                 <Button
                   variant="contained"
                   color="primary"
                   onClick={handlePartsInventoryExport}
                   disabled={loading}
                   style={{ height: '56px', minWidth: '150px' }}
                 >
                   {loading ? <CircularProgress size={24} /> : 'Export Parts CSV'}
                 </Button>
               </div>
             </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
                 <Grid container spacing={3} style={{ marginBottom: '30px' }}>
           {summaryCards.map((card, index) => (
             <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={index}>
              <Card style={{ backgroundColor: card.color, color: 'white' }}>
                <CardContent>
                  <Typography variant="h4" component="h2" gutterBottom>
                    {card.value}
                  </Typography>
                  <Typography variant="h6" component="h3">
                    {card.title}
                  </Typography>
                  <Typography variant="body2" style={{ marginTop: '8px', opacity: 0.9 }}>
                    {card.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Time Series Controls */}
        <Card style={cardStyles}>
          <CardContent style={{ padding: '32px', backgroundColor: '#fafafa' }}>
            <Typography variant="h6" gutterBottom style={{ color: '#1976d2', marginBottom: '20px', fontWeight: 'bold', fontSize: '20px', textAlign: 'center' }}>
              📈 CHART CONTROLS
            </Typography>
                         <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
               {/* First row - All inputs when there's space, otherwise wrap */}
               <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'flex-end' }}>
                 <FormControl style={{ minWidth: '200px', flex: '0 0 auto' }}>
                   <InputLabel style={{ color: '#1976d2', fontWeight: 'bold' }}>Interval</InputLabel>
                   <Select
                     value={interval}
                     onChange={(e) => handleIntervalChange(e.target.value)}
                     style={controlStyles}
                     MenuProps={{
                       PaperProps: {
                         style: {
                           backgroundColor: '#ffffff',
                           color: '#333333',
                           border: '2px solid #1976d2',
                           boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
                         }
                       }
                     }}
                   >
                     <MenuItem value="day" style={{ color: '#333333' }}>Daily</MenuItem>
                     <MenuItem value="week" style={{ color: '#333333' }}>Weekly</MenuItem>
                     <MenuItem value="month" style={{ color: '#333333' }}>Monthly</MenuItem>
                     <MenuItem value="year" style={{ color: '#333333' }}>Yearly</MenuItem>
                   </Select>
                 </FormControl>
                 
                 {showTimeRangeControls && (
                   <>
                     <FormControl style={{ minWidth: '200px', flex: '0 0 auto' }}>
                       <InputLabel style={{ color: '#1976d2', fontWeight: 'bold' }}>Time Range</InputLabel>
                       <Select
                         value={timeRange}
                         onChange={(e) => handleTimeRangeChange(e.target.value)}
                         style={controlStyles}
                         MenuProps={{
                           PaperProps: {
                             style: {
                               backgroundColor: '#ffffff',
                               color: '#333333',
                               border: '2px solid #1976d2',
                               boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
                             }
                           }
                         }}
                       >
                         {getTimeRangeOptions().map(option => (
                           <MenuItem key={option.value} value={option.value} style={{ color: '#333333' }}>
                             {option.label}
                           </MenuItem>
                         ))}
                       </Select>
                     </FormControl>
                     
                     <TextField
                       label="Start Date"
                       type="date"
                       value={startDate}
                       onChange={(e) => setStartDate(e.target.value)}
                       InputLabelProps={{ shrink: true }}
                       style={{ ...controlStyles, minWidth: '180px', flex: '0 0 auto' }}
                       InputProps={{
                         style: { color: '#333333' }
                       }}
                     />
                     
                     <TextField
                       label="End Date"
                       type="date"
                       value={endDate}
                       onChange={(e) => setEndDate(e.target.value)}
                       InputLabelProps={{ shrink: true }}
                       style={{ ...controlStyles, minWidth: '180px', flex: '0 0 auto' }}
                       InputProps={{
                         style: { color: '#333333' }
                       }}
                     />
                   </>
                 )}
               </div>

               {/* Second row - Buttons only */}
               {showTimeRangeControls && (
                 <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
                   <Button
                     variant="contained"
                     color="primary"
                     onClick={fetchTimeSeries}
                     disabled={loading}
                     style={{ height: '56px', minWidth: '140px' }}
                   >
                     {loading ? <CircularProgress size={24} /> : 'Update Charts'}
                   </Button>
                   <Button
                     variant="contained"
                     color="secondary"
                     onClick={handleChartDataExport}
                     disabled={loading || timeSeriesData.length === 0}
                     style={{ height: '56px', minWidth: '120px' }}
                   >
                     {loading ? <CircularProgress size={24} /> : 'Export CSV'}
                   </Button>
                 </div>
               )}
             </div>
          </CardContent>
        </Card>

        {/* Charts */}
        <Grid container spacing={3}>
          {/* Total Value Chart */}
          <Grid item xs={12} lg={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Total Value On Hand Over Time
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={timeSeriesData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="summaryDate" 
                      tickFormatter={(date) => format(new Date(date), 'MMM yyyy')}
                    />
                    <YAxis 
                      {...getTotalValueChartYAxisProps()}
                    />
                    <Tooltip 
                      formatter={(value) => [formatCurrency(value), 'Total Value']}
                      labelFormatter={(date) => format(new Date(date), 'MMM dd, yyyy')}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="totalValue" 
                      stroke="#9c27b0" 
                      strokeWidth={2}
                      name="Total Value"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Packaged vs Unpackaged Chart */}
          <Grid item xs={12} lg={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Packaged vs Unpackaged Value
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={timeSeriesData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="summaryDate" 
                      tickFormatter={(date) => format(new Date(date), 'MMM yyyy')}
                    />
                    <YAxis 
                      {...getPackagedUnpackagedChartYAxisProps()}
                    />
                    <Tooltip 
                      formatter={(value) => [formatCurrency(value), 'Value']}
                      labelFormatter={(date) => format(new Date(date), 'MMM dd, yyyy')}
                    />
                    <Legend />
                                         <Line 
                       type="monotone" 
                       dataKey="packagedValue" 
                       stroke="#4caf50" 
                       strokeWidth={2}
                       name="Packaged Value"
                     />
                     <Line 
                       type="monotone" 
                       dataKey="unpackagedValue" 
                       stroke="#ff9800" 
                       strokeWidth={2}
                       name="Unpackaged Value"
                     />
                     <Line 
                       type="monotone" 
                       dataKey="assembliesMarketValue" 
                       stroke="#9c27b0" 
                       strokeWidth={2}
                       name="Packaged Assembly Value"
                     />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

                     {/* Quantity Chart */}
           <Grid item xs={12}>
             <Card>
               <CardContent>
                 <Typography variant="h6" gutterBottom>
                   Inventory Quantities Over Time
                 </Typography>
                 <ResponsiveContainer width="100%" height={300}>
                   <LineChart data={timeSeriesData}>
                     <CartesianGrid strokeDasharray="3 3" />
                     <XAxis 
                       dataKey="summaryDate" 
                       tickFormatter={(date) => format(new Date(date), 'MMM yyyy')}
                     />
                                          <YAxis 
                        {...getQuantityChartYAxisProps()}
                      />
                     <Tooltip 
                       formatter={(value) => [formatNumber(value), 'Quantity']}
                       labelFormatter={(date) => format(new Date(date), 'MMM dd, yyyy')}
                     />
                     <Legend />
                                          <Line 
                        type="monotone" 
                        dataKey="partsPackaged" 
                        stroke="#1976d2" 
                        strokeWidth={2}
                        name="Total Packaged"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="partsUnpackaged" 
                        stroke="#f50057" 
                        strokeWidth={2}
                        name="Total Unpackaged"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="assembliesPackaged" 
                        stroke="#4caf50" 
                        strokeWidth={2}
                        name="Packaged Assembly Count"
                      />
                   </LineChart>
                 </ResponsiveContainer>
               </CardContent>
             </Card>
           </Grid>

           {/* Average Weeks Inventory Chart */}
           {averageWeeksInventoryTimeSeriesData && averageWeeksInventoryTimeSeriesData.dataPoints.length > 0 && (
             <Grid item xs={12}>
               <Card>
                 <CardContent>
                   <Typography variant="h6" gutterBottom>
                     Average Weeks On Hand Over Time
                   </Typography>
                   <ResponsiveContainer width="100%" height={300}>
                     <LineChart data={averageWeeksInventoryTimeSeriesData.dataPoints}>
                       <CartesianGrid strokeDasharray="3 3" />
                       <XAxis 
                         dataKey="calculationDate" 
                         tickFormatter={(date) => format(new Date(date), 'MMM yyyy')}
                       />
                       <YAxis 
                         domain={[0, 'auto']}
                         tickFormatter={(value) => `${value.toFixed(1)}`}
                       />
                       <Tooltip 
                         formatter={(value) => [`${value.toFixed(2)} weeks`, 'Average Weeks']}
                         labelFormatter={(date) => format(new Date(date), 'MMM dd, yyyy')}
                       />
                       <Legend />
                       <Line 
                         type="monotone" 
                         dataKey="averageWeeksOnHand" 
                         stroke="#673ab7" 
                         strokeWidth={2}
                         name="Average Weeks on Hand"
                       />
                     </LineChart>
                   </ResponsiveContainer>
                 </CardContent>
               </Card>
             </Grid>
           )}
        </Grid>

        {/* Error Snackbar */}
        <Snackbar
          open={snackbarOpen}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
        >
          <Alert onClose={() => setSnackbarOpen(false)} severity="error">
            {error}
          </Alert>
        </Snackbar>

        {/* Average Weeks Inventory Dialog */}
        <Dialog
          open={averageWeeksInventoryOpen}
          onClose={() => setAverageWeeksInventoryOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle style={{ backgroundColor: '#1976d2', color: 'white' }}>
            📊 Select Parts for Average Weeks Inventory Calculation
          </DialogTitle>
          <DialogContent style={{ padding: '24px' }}>
            {partsListLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                <CircularProgress />
              </div>
            ) : (
              <>
                {/* Search and Selection Controls */}
                <div style={{ marginBottom: '16px' }}>
                  <TextField
                    label="Search Parts"
                    value={partsSearchTerm}
                    onChange={(e) => setPartsSearchTerm(e.target.value)}
                    style={{ width: '100%', marginBottom: '8px' }}
                    placeholder="Search by part ID or description..."
                  />
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleSelectAll}
                    >
                      Select All
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleSelectNone}
                    >
                      Select None
                    </Button>
                  </div>
                </div>

                {/* Parts List */}
                <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', padding: '8px' }}>
                  <List dense>
                    {getFilteredParts().map((part) => (
                      <ListItem key={part.part.id} style={{ padding: '4px 0' }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={averageWeeksInventoryPartFilters[part.part.id] || false}
                              onChange={(e) => handlePartFilterChange(part.part.id, e.target.checked)}
                              color="primary"
                            />
                          }
                          label={
                            <div>
                              <Typography variant="body2">
                                {part.part.partId} rev {part.part.revision}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {part.part.description}
                              </Typography>
                            </div>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </div>
              </>
            )}
          </DialogContent>
          <DialogActions style={{ padding: '16px 24px' }}>
            <Button
              onClick={() => setAverageWeeksInventoryOpen(false)}
              color="primary"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </DashboardTemplate>
  );
};

export default Analytics;
